import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import ChatPanel from '../components/ChatPanel';
import RightPanel from '../components/RightPanel';
import RelationPanel from '../components/RelationPanel';
import {
  createRelationChat,
  createRelationSession,
  deleteRelationChat,
  deleteRelationSession,
  getRelationChats,
  getRelationSessions,
  updateRelationSessionActiveChat,
} from '../services/api';

export default function DashboardPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const pendingConnection = location.state?.newConnection || null;
  const autoOpenConnectModal = !!location.state?.openConnectModal;
  const consumePendingConnection = useCallback(() => {
    navigate(location.pathname, { replace: true, state: {} });
  }, [navigate, location.pathname]);

  const [activeConnection, setActiveConnection] = useState(null);
  const [activeChat, setActiveChat] = useState(null);
  const [activeMode, setActiveMode] = useState('chat');

  const [relationSessions, setRelationSessions] = useState([]);
  const [relationChats, setRelationChats] = useState([]);
  const [activeRelationSession, setActiveRelationSession] = useState(null);
  const [activeRelationChat, setActiveRelationChat] = useState(null);
  const [expandedRelationSession, setExpandedRelationSession] = useState(null);
  const skipNextRelationChatLoadRef = useRef(false);

  const handleChatRenamed = useCallback((updatedChat) => {
    setActiveChat(prev => (prev?.id === updatedChat.id ? updatedChat : prev));
  }, []);

  const handleRelationChatRenamed = useCallback((updatedChat) => {
    setRelationChats(prev => prev.map(chat => (chat.id === updatedChat.id ? updatedChat : chat)));
    setActiveRelationChat(prev => (prev?.id === updatedChat.id ? updatedChat : prev));
  }, []);

  const handleSelectConnection = useCallback((conn) => {
    setActiveMode('chat');
    setActiveConnection(conn);
  }, []);

  const handleSelectChat = useCallback((chat) => {
    setActiveMode('chat');
    setActiveChat(chat);
  }, []);

  const handleStartRelation = useCallback(() => {
    setActiveMode('relation');
    if (!activeRelationSession && relationSessions.length > 0) {
      setActiveRelationSession(relationSessions[0]);
    }
  }, [activeRelationSession, relationSessions]);

  const handleSelectRelationSession = useCallback((session) => {
    setActiveMode('relation');
    setActiveRelationChat(null);
    setRelationChats([]);
    setActiveRelationSession(session);
    setExpandedRelationSession(session?.id || null);
  }, []);

  const handleSelectRelationChat = useCallback(async (chat) => {
    if (!chat) {
      setActiveRelationChat(null);
      return;
    }
    setActiveMode('relation');
    setActiveRelationChat(chat);
    if (activeRelationSession?.id) {
      await updateRelationSessionActiveChat(activeRelationSession.id, chat.id);
      setRelationSessions(prev => prev.map(session => (
        session.id === activeRelationSession.id
          ? { ...session, active_chat_id: chat.id }
          : session
      )));
      setActiveRelationSession(prev => (prev?.id === activeRelationSession.id ? { ...prev, active_chat_id: chat.id } : prev));
    }
  }, [activeRelationSession?.id]);

  const loadRelationSessions = useCallback(async () => {
    const sessions = await getRelationSessions();
    setRelationSessions(prev => {
      const same = JSON.stringify(prev) === JSON.stringify(sessions);
      return same ? prev : sessions;
    });
    setActiveRelationSession(current => current || sessions[0] || null);
    setExpandedRelationSession(current => current || sessions[0]?.id || null);
    if (sessions.length === 0) {
      setActiveRelationSession(null);
      setActiveRelationChat(null);
      setRelationChats([]);
      setExpandedRelationSession(null);
    }
  }, []);

  const loadRelationChats = useCallback(async (session) => {
    if (!session?.id) {
      setRelationChats([]);
      setActiveRelationChat(null);
      return;
    }
    const chats = await getRelationChats(session.id);
    setRelationChats(prev => {
      const same = JSON.stringify(prev) === JSON.stringify(chats);
      return same ? prev : chats;
    });
    const preferred = chats.find(chat => chat.id === session?.active_chat_id) || chats[0] || null;
    setActiveRelationChat(prev => (prev?.id === preferred?.id ? prev : preferred));
  }, []);

  useEffect(() => {
    loadRelationSessions().catch(() => {});
  }, [loadRelationSessions]);

  useEffect(() => {
    if (!activeRelationSession?.id) {
      setRelationChats([]);
      setActiveRelationChat(null);
      return;
    }
    if (skipNextRelationChatLoadRef.current) {
      skipNextRelationChatLoadRef.current = false;
      return;
    }
    loadRelationChats(activeRelationSession).catch(() => {});
  }, [activeRelationSession?.id, loadRelationChats]);

  const handleCreateRelationSession = useCallback(async (uploadResult) => {
    const session = await createRelationSession({
      draft_id: uploadResult.draft_id,
      name: uploadResult.name || 'Relation Session',
      table1_name: uploadResult.table1.name,
      table2_name: uploadResult.table2.name,
      columns1: uploadResult.table1.columns,
      columns2: uploadResult.table2.columns,
      join_key: uploadResult.join_key,
      file_is_unreliable: uploadResult.file_is_unreliable || false,
    });
    setRelationSessions(prev => [session, ...prev.filter(s => s.id !== session.id)]);
    setActiveRelationSession(session);
    setExpandedRelationSession(session.id);

    skipNextRelationChatLoadRef.current = true;
    const chat = await createRelationChat({ relation_session_id: session.id, title: 'New Chat' });
    setRelationChats([chat]);
    setActiveRelationChat(chat);
    setRelationSessions(prev => prev.map(s => (s.id === session.id ? { ...s, active_chat_id: chat.id } : s)));
    setActiveRelationSession(prev => (prev?.id === session.id ? { ...prev, active_chat_id: chat.id } : { ...session, active_chat_id: chat.id }));
    return { session: { ...session, active_chat_id: chat.id }, chat };
  }, []);

  const handleCreateRelationChat = useCallback(async () => {
    if (!activeRelationSession?.id) return null;
    const chat = await createRelationChat({ relation_session_id: activeRelationSession.id, title: 'New Chat' });
    setRelationChats(prev => [chat, ...prev]);
    setActiveRelationChat(chat);
    setRelationSessions(prev => prev.map(s => (s.id === activeRelationSession.id ? { ...s, active_chat_id: chat.id } : s)));
    setActiveRelationSession(prev => (prev?.id === activeRelationSession.id ? { ...prev, active_chat_id: chat.id } : prev));
    return chat;
  }, [activeRelationSession?.id, relationSessions]);

  const handleDeleteRelationSession = useCallback(async (sessionId) => {
    await deleteRelationSession(sessionId);
    setRelationSessions(prev => prev.filter(session => session.id !== sessionId));
    if (activeRelationSession?.id === sessionId) {
      const nextSession = relationSessions.find(session => session.id !== sessionId) || null;
      setActiveRelationSession(nextSession);
      setActiveRelationChat(null);
      setRelationChats([]);
      setExpandedRelationSession(nextSession?.id || null);
    }
  }, [activeRelationSession?.id]);

  const handleDeleteRelationChat = useCallback(async (chatId) => {
    await deleteRelationChat(chatId);
    setRelationChats(prev => prev.filter(chat => chat.id !== chatId));
    if (activeRelationChat?.id === chatId) {
      const remaining = relationChats.filter(chat => chat.id !== chatId);
      const next = remaining[0] || null;
      setActiveRelationChat(next);
      if (activeRelationSession?.id) {
        await updateRelationSessionActiveChat(activeRelationSession.id, next?.id || null);
        setRelationSessions(prev => prev.map(session => (
          session.id === activeRelationSession.id
            ? { ...session, active_chat_id: next?.id || null }
            : session
        )));
        setActiveRelationSession(prev => (prev?.id === activeRelationSession.id ? { ...prev, active_chat_id: next?.id || null } : prev));
      }
    }
  }, [activeRelationChat?.id, relationChats, activeRelationSession?.id]);

  const handleRelationSessionOpened = useCallback((session) => {
    setExpandedRelationSession(session?.id || null);
    handleSelectRelationSession(session);
  }, [handleSelectRelationSession]);

  return (
    <div style={s.layout}>
      <Sidebar
        activeConnection={activeConnection}
        onSelectConnection={handleSelectConnection}
        pendingConnection={pendingConnection}
        onConsumePendingConnection={consumePendingConnection}
        autoOpenConnectModal={autoOpenConnectModal}
        onConsumeAutoOpenConnectModal={consumePendingConnection}
        activeChat={activeChat}
        onSelectChat={handleSelectChat}
        onNewChat={handleSelectChat}
        activeMode={activeMode}
        onStartRelation={handleStartRelation}
        relationSessions={relationSessions}
        relationChats={relationChats}
        activeRelationSession={activeRelationSession}
        activeRelationChat={activeRelationChat}
        expandedRelationSession={expandedRelationSession}
        onSelectRelationSession={handleRelationSessionOpened}
        onSelectRelationChat={handleSelectRelationChat}
        onNewRelationChat={handleCreateRelationChat}
        onDeleteRelationSession={handleDeleteRelationSession}
        onDeleteRelationChat={handleDeleteRelationChat}
        onSetExpandedRelationSession={setExpandedRelationSession}
      />
      {activeMode === 'chat' ? (
        <ChatPanel
          chat={activeChat}
          connection={activeConnection}
          onChatRenamed={handleChatRenamed}
        />
      ) : (
        <RelationPanel
          relationSession={activeRelationSession}
          relationChat={activeRelationChat}
          onChatRenamed={handleRelationChatRenamed}
          onCreateSession={handleCreateRelationSession}
          onCreateChat={handleCreateRelationChat}
          onSelectSession={handleRelationSessionOpened}
          onSelectChat={handleSelectRelationChat}
        />
      )}
      <RightPanel
        connection={activeConnection}
        activeMode={activeMode}
        relationSession={activeRelationSession}
      />
    </div>
  );
}

const s = {
  layout: {
    display: 'flex', height: '100vh', overflow: 'hidden',
    background: 'var(--bg-primary)',
  },
};
